import { type DocumentReference } from 'firebase/firestore';

import { hostingStoragePathToUrl } from 'services/Firebase';
import { getAppBroker } from 'stores/AppBroker';
import * as DatabasePaths from 'stores/DatabasePaths';
import { AdEvents } from 'types/AdEvents';
import { DocumentCollectionType } from 'types/DocumentCollectionType';
import { DocumentType } from 'types/DocumentType';
import { deepCopy } from 'utils/Copy';
import { serverTimestamp } from 'utils/FirestoreUtils';
import { walkValue } from 'utils/Walk';

import CommentsManager from './CommentsManager';
import { DocumentSchema } from './DocumentSchema';
import DocumentStore from './DocumentStore';
import type TearsheetAdsManager from './TearsheetAdsManager';

export class TearsheetAdSchema extends DocumentSchema {
    dynamicData: unknown = {};
    id: string = '';
    index = NaN;
    label = '';
    dimensions = '';
    type = DocumentType.TEARSHEET_AD;
    numComments: number = 0;
    viewHistory: Record<string, { seconds: number }> = {};
    lastModified = serverTimestamp();
}

function isHostedStoragePath(v: string): boolean {
    return /(^|\/)hostedstorage\//.test(v);
}

function transformDynamicDataInPlace(target: any) {
    walkValue(target, (parent, key, value) => {
        if (typeof value === 'string' && isHostedStoragePath(value)) {
            parent[key] = hostingStoragePathToUrl(value);
            return false;
        }
        return true;
    });
}

export default class TearsheetAd extends DocumentStore<TearsheetAdSchema> {
    schema = new TearsheetAdSchema();
    type = DocumentType.AD;
    collectionType = DocumentCollectionType.AD;

    active = false;
    isRead = true;
    comments: CommentsManager = new CommentsManager(this);

    previewData() {
        const data = deepCopy(this.data.dynamicData);
        transformDynamicDataInPlace(data);
        return data;
    }

    get numComments() {
        return this.data.numComments;
    }

    constructor(parent: TearsheetAdsManager, docRef?: DocumentReference) {
        super(parent, docRef);
        this.comments.enable();
        this.becomeObservable({ except: ['properties', 'comments'] });
    }

    get dimensions() {
        return this.data.dimensions;
    }

    // The getter names are wordy on purpose: `index` has meant several
    // different things in this code, and mixing them up has caused real bugs.
    //
    // 'nominal' means a property of the ad itself, as opposed to 'intrinsic':
    // where the ad happens to sit in an array. Never subscript `children`
    // with a nominal index.

    get nominal0BasedIndex(): number {
        // The creative ad's sort key, copied when the tearsheet was made. The
        // creative keeps its own keys dense from 0, but nothing maintains the
        // copies, so in a tearsheet they can start anywhere and have gaps.
        return this.data.index;
    }

    get nominal1BasedIndex(): number {
        // The number the creative's ad list showed for this ad when the
        // tearsheet was made. Tearsheet URLs have carried it since 2022, and
        // those links are shared with producers and clients, so it stays.
        return this.nominal0BasedIndex + 1;
    }

    navigateTo = () => {
        // DatabasePaths.tearsheetAd has an :adIndex placeholder, but that
        // will pick up this ad's firestore id, whereas URLs carry
        // nominal1BasedIndex. So we don't destructure `adIndex`.
        const { clientID, projectID, creativeID, tearsheetID } =
            DatabasePaths.tearsheetAd.requireMatch(this.docRef.path);

        // instead we replace it thus
        const adIndex = String(this.nominal1BasedIndex);

        void getAppBroker().navigate({
            clientID,
            projectID,
            creativeID,
            tearsheetID,
            adIndex,
        });
    };

    get label() {
        return this.data.label;
    }

    activate = async () => {
        this.active = true;
        if (!this.comments.loaded) await this.comments.load();
    };

    reload = () => {
        console.log('ad reload');
        this.dispatch(AdEvents.RELOAD);
    };

    deactivate() {
        this.active = false;
    }
}
